<div mat-dialog-title fxLayoutAlign="space-between center">
  <h2>{{ ('dialog.pull-pre-auth.title.' + data.type)| translate }}</h2>
  <span fxFlex></span>
  <span *ngIf="data.appointment?.startAt" class="mr-3">
    {{ 'dialog.pull-pre-auth.dos' | translate: { value: data.appointment.startAt | momentDate } }}
  </span>
  <span *ngIf="procedureCodes.length > 0">
    CPTs: {{ procedureCodes.join(', ') }}
  </span>
</div>

<mat-progress-bar *ngIf="loading$ | async" mode="indeterminate" class="session-progress"></mat-progress-bar>

<form *ngIf="availableInsurances$ | async as insurances" [formGroup]="formGroup">
  <mat-dialog-content class="py-4">
    <div fxLayout="column">
      <ng-container *ngIf="(mode$ | async) === 'request'">
        <mat-form-field fxFlex>
          <mat-label>
            {{ 'service-review-inquiry.form.insurance' | translate }}
          </mat-label>
          <mat-select required formControlName="insurance" (selectionChange)="onInsuranceChange()">
            <mat-option *ngFor="let insurance of insurances" [value]="insurance.value" [disabled]="insurance.disabled">
              {{ insurance.viewValue }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field fxFlex class="mb-3">
          <mat-label>
            {{ 'common.facility' | translate }}
          </mat-label>
          <mmx-facility-selector-input formControlName="facility"></mmx-facility-selector-input>
          <mat-hint *ngIf="conflictingFacility$ | async">
            {{ 'service-review-inquiry.form.facility-conflict' | translate }}
          </mat-hint>
        </mat-form-field>

        <mat-form-field fxFlex class="mb-3">
          <mat-label>
            {{ 'common.referrer-npi' | translate }}
          </mat-label>
          <input
            matInput
            type="text"
            formControlName="orderingPhysicianNPI"
            [matAutocomplete]="autoReferrer"
          />
          <mat-autocomplete #autoReferrer="matAutocomplete">
            <mat-option
              *ngFor="let referrer of filteredReferrers$ | async"
              [value]="referrer.npi"
            >
              {{ referrer.viewValue }}
            </mat-option>
          </mat-autocomplete>
          <mat-hint>{{ 'service-review-inquiry.form.referrer.hint' | translate }}</mat-hint>
          <mat-error *ngIf="formGroup.get('orderingPhysicianNPI').invalid">
            Invalid NPI
          </mat-error>
        </mat-form-field>

        <mat-form-field *ngIf="availableServiceTypes?.length > 1" fxFlex class="mb-3">
          <mat-label>
            {{ 'service-review-inquiry.form.request-type.label' | translate }}
          </mat-label>
          <mat-select formControlName="requestTypeCode">
            <mat-option *ngIf="availableServiceTypes?.includes(SUBTYPE.HEALTH_SERVICES)" [value]="SUBTYPE.HEALTH_SERVICES">Outpatient Authorizations / Health Services Review</mat-option>
            <mat-option *ngIf="availableServiceTypes?.includes(SUBTYPE.ADMISSION)" [value]="SUBTYPE.ADMISSION">Inpatient Authorizations / Admission Review</mat-option>
            <mat-option *ngIf="availableServiceTypes?.includes(SUBTYPE.REFERRALS)" [value]="SUBTYPE.REFERRALS">Referrals / Specialty Care Review</mat-option>
          </mat-select>
          <mat-hint>{{ 'service-review-inquiry.form.request-type.hint' | translate }}</mat-hint>
        </mat-form-field>

        <mat-form-field fxFlex class="mb-5">
          <mat-label>{{ 'service-review-inquiry.form.portal-rules.label' | translate }}</mat-label>
          <mat-select multiple formControlName="authRules" [compareWith]="compareAuthRules" required>
            <mat-option *ngFor="let rule of authRules$ | async" [value]="rule.value" [disabled]="rule.disabled">
              {{ rule.viewValue }}
            </mat-option>
          </mat-select>
          <mat-hint>
            {{ 'service-review-inquiry.form.portal-rules.hint' | translate }}
            <strong>{{ defaultAuthRuleName }}</strong>
          </mat-hint>
        </mat-form-field>

        <ng-container *ngIf="supportsSubscriberOverride">
          <mat-form-field fxFlex>
            <mat-label>
              {{ 'service-review-inquiry.form.subscriber-relationship' | translate }}
            </mat-label>
            <mat-select formControlName="relationship">
              <mat-option *ngFor="let relationship of relationshipOptions" [value]="relationship">
                {{ ('insurances.relationship.' + relationship) | translate }}
              </mat-option>
            </mat-select>
            <mat-hint>{{ 'service-review-inquiry.form.subscriber-hint' | translate }}</mat-hint>
          </mat-form-field>

          <div *ngIf="showsSubscriber" class="mb-3" fxLayout="row" fxLayoutGap="1rem" fxLayoutAlign="space-between start">
            <mat-form-field fxFlex>
              <mat-label>
                {{ 'dialog.insurance.subscriber-first-name' | translate }}
              </mat-label>
              <input matInput formControlName="subscriberFirstName" />
            </mat-form-field>

            <mat-form-field fxFlex>
              <mat-label>
                {{ 'dialog.insurance.subscriber-last-name' | translate }}
              </mat-label>
              <input matInput formControlName="subscriberLastName" />
            </mat-form-field>
          </div>

          <div *ngIf="subscriberMissing" class="mb-3 text-warn">
            {{ 'service-review-inquiry.form.subscriber-missing' | translate }}
          </div>
        </ng-container>

        <mmx-service-review-inquiry-form
          formControlName="priorAuthorization"
          [type]="data.type"
          [products]="data.products"
          [appointment]="data.appointment"
        ></mmx-service-review-inquiry-form>
      </ng-container>

      <ng-container *ngIf="serviceReviews$ | async as serviceReviews">
        <mmx-prior-authorization-list
          *ngIf="resultsVisible$ | async"
          [serviceReviews]="serviceReviews"
          formControlName="serviceReviews"
        ></mmx-prior-authorization-list>
      </ng-container>
    </div>

    <ng-container *ngIf="serviceReviewErrors$ | async as portalErrors">
      <div *ngIf="errorsVisible$ | async" class="errors-container">
        <h6 class="mt-0 mb-4">{{ 'dialog.pull-pre-auth.errors.title' | translate }}</h6>
        <ng-container *ngFor="let portal of portalErrors">
          <ng-container *ngIf="portal.errors?.length">
            <div class="mt-0 mb-2">
              <strong>{{ portal.api | authorizationRuleAPIName | translate }}:</strong>
            </div>
            <ul class="errors-list m-0 mb-4">
              <li *ngFor="let error of portal.errors">
                <ng-container *ngIf="error.code">
                  [{{ error.code }}]
                </ng-container>
                {{ error.errorMessage }}
              </li>
            </ul>
          </ng-container>
        </ng-container>
      </div>
    </ng-container>

    <ul *ngIf="noSelection" class="errors-list">
      <li>No selection has been made. Please select at least one item to import.</li>
    </ul>
  </mat-dialog-content>

  <ul *ngIf="noSelection" class="errors-list">
    <li>No selection has been made. Please select at least one item to import.</li>
  </ul>

  <mat-dialog-actions fxLayout="row" fxLayoutAlign="end center" fxLayoutAlign="space-between center">
    <ng-container *ngIf="mode$ | async as mode">
      <ng-container *ngIf="mode === 'request'">
        <button mat-flat-button mat-dialog-close>
          {{ 'dialog.pull-pre-auth.actions.cancel' | translate }}
        </button>

        <button
          mat-flat-button
          type="submit"
          color="primary"
          [disabled]="loading$ | async"
          (click)="onSubmit(mode)"
        >
          {{ 'dialog.pull-pre-auth.actions.pull' | translate }}
        </button>
      </ng-container>

      <ng-container *ngIf="mode === 'results'">
        <button mat-flat-button (click)="goBack()">
          {{ 'dialog.pull-pre-auth.actions.back' | translate }}
        </button>

        <button
          mat-flat-button
          type="submit"
          color="primary"
          [disabled]="loading$ | async"
          (click)="onSubmit(mode)"
        >
          {{ 'dialog.pull-pre-auth.actions.save' | translate }}
        </button>
      </ng-container>
    </ng-container>
  </mat-dialog-actions>
</form>
